import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlass, X, User, CaretDown } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Filter list-page records by their owner(s). Multi-select via chips, with
 * an in-menu search box for large teams. Auto-hides for roles that can only
 * see their own records — the backend endpoint returns an empty owners list
 * for them, so we simply render nothing.
 *
 * Props:
 *   value      — array of selected owner_ids (controlled)
 *   onChange   — (newIds: string[]) => void
 *   testid     — data-testid prefix (default 'owner-filter')
 */
export default function OwnerFilter({ value = [], onChange, testid = 'owner-filter' }) {
  const [owners, setOwners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/team/searchable-owners`)
      .then((r) => setOwners(r.data.owners || []))
      .catch((err) => {
        // Distinguish "no auth / server error" from the legitimate empty-list
        // (Sales Exec) case — the latter comes back as 200 with owners=[].
        // Log to console so a silent auto-hide doesn't mask a real API break.
        console.warn('[OwnerFilter] failed to load owners:', err?.response?.status, err?.message);
        setOwners([]);
      })
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const byId = useMemo(() => {
    const m = {};
    owners.forEach((o) => { m[o.id] = o; });
    return m;
  }, [owners]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter((o) => {
      const hay = `${o.name || ''} ${o.email || ''} ${o.role || ''} ${o.region || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [owners, search]);

  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  const clearAll = () => onChange([]);

  // Hide entirely when the current user isn't a manager (empty owners) —
  // avoids showing a filter widget that would be a no-op.
  if (!loaded) return null;
  if (owners.length <= 1) return null;

  const summary =
    value.length === 0 ? 'All owners'
    : value.length === 1 ? (byId[value[0]]?.name || '1 owner')
    : `${value.length} owners selected`;

  return (
    <div className="relative inline-block" ref={rootRef} data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition ${
          value.length > 0
            ? 'border-[#00a69c] text-[#00a69c] bg-[#E2F3F0]'
            : 'border-[#D5E8E4] text-[#3B5A54] bg-white hover:border-[#00a69c] hover:text-[#00a69c]'
        }`}
        data-testid={`${testid}-trigger`}
        title="Filter by owner"
      >
        <User size={14} weight="duotone" />
        <span className="max-w-[180px] truncate">{summary}</span>
        <CaretDown size={11} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {value.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-red-500 hover:text-red-700"
          data-testid={`${testid}-clear`}
          title="Clear all"
        >
          <X size={10} weight="bold" /> clear
        </button>
      )}

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-[#E2F3F0] z-40 overflow-hidden"
          data-testid={`${testid}-panel`}
        >
          <div className="p-2 border-b border-[#E2F3F0]">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-[#6D8E87]" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, region…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-[#D5E8E4] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00a69c]"
                data-testid={`${testid}-search`}
              />
            </div>
          </div>
          {value.length > 0 && (
            <div className="px-3 py-2 border-b border-[#E2F3F0] bg-[#F4F9F7] flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">
                {value.length} selected
              </span>
              <button onClick={clearAll} className="text-[10px] text-red-500 hover:text-red-700 font-semibold" data-testid={`${testid}-panel-clear`}>
                Clear all
              </button>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-[#6D8E87] text-center italic">
                No owner matches "{search}"
              </div>
            ) : filtered.map((o) => {
              const on = value.includes(o.id);
              return (
                <button
                  key={o.id}
                  onClick={() => toggle(o.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F4F9F7] transition text-sm ${on ? 'bg-[#E2F3F0]' : ''}`}
                  data-testid={`${testid}-item-${o.id}`}
                >
                  <div className={`w-4 h-4 rounded border-2 grid place-items-center shrink-0 ${on ? 'bg-[#00a69c] border-[#00a69c]' : 'border-[#D5E8E4]'}`}>
                    {on && <X size={10} className="text-white rotate-45" weight="bold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#052A23] truncate">{o.name || '—'}</div>
                    <div className="text-[10px] text-[#6D8E87] truncate">
                      {o.role || '—'}{o.region ? ` · ${o.region}` : ''}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
